import { inject, injectable } from "inversify";
import { AjaxService, DataGridService, FormService, LoaderService, MessageService, PageService, ScriptService, TierSelectorService, UrlService, UserTierService, WizardService } from ".";
import { IpfModule } from "..";
import { NcqLogLevel } from "../enums.generated";
import { TYPES } from "../ioc/types";
import { LogMessage } from "../models/app";
import { HttpMethods } from "../models/http";
import { ServiceBase } from "./ServiceBase";

/**
 * IPF page service, used to setup the page and execute scripts.
 * This service is key to the lifecyle of JavaScript when loading pages.
 */
@injectable()
export class IpfPageService extends ServiceBase implements PageService {

	/**
	 * Initialises a new instance of the IpfPageService class.
	 * @param ajaxService AJAX service, used to make AJAX HTTP requests.
	 * @param formService Form service, used to manage forms.
	 * @param dataGridService Data grid service, used to render data grids.
	 * @param ipfModule IPF module, containing contextual information and functions.
	 * @param loaderService Loader service, used to show loading elements.
	 * @param messageService Message service, used to display messages.
	 * @param scriptService Script service, used to execute scripts.
	 * @param tierSelectorService Tier selector service, used to apply tier selectors to input elements.
	 * @param wizardService Wizard service, used to setup form wizards.
	 * @param urlService URL service, used to generate URLs.
	 * @param userTierService User tier service, used to apply tier params to URLs.
	 */
	public constructor(
		@inject(TYPES.AjaxService) private ajaxService: AjaxService,
		@inject(TYPES.FormService) private formService: FormService,
		@inject(TYPES.DataGridService) private dataGridService: DataGridService,
		@inject(TYPES.IpfModule) private ipfModule: IpfModule,
		@inject(TYPES.LoaderService) private loaderService: LoaderService,
		@inject(TYPES.MessageService) private messageService: MessageService,
		@inject(TYPES.ScriptService) private scriptService: ScriptService,
		@inject(TYPES.TierSelectorService) private tierSelectorService: TierSelectorService,
		@inject(TYPES.WizardService) private wizardService: WizardService,
		@inject(TYPES.UrlService) private urlService: UrlService,
		@inject(TYPES.UserTierService) private userTierService: UserTierService
	) {
		super();
	}

	/**
	 * @inheritdoc
	 */
	ready() {

		const pageLoaderTimeout = setTimeout(() => {
			this.loaderService.showPageLoader();
		}, 1000);

		const self = this;

		try {

			document.querySelector("body")?.classList.add("page-service-ready");

			this.scriptService.register(() => {

				document.querySelector("body")?.classList.add("page-service-ready-register");

				this.ipfModule.onReady.forEach(handler => handler());

				this.ajaxService.unobstrusive(document.body);

				this.applyEffects(document.body);

				this.applySearch();
			});

			this.scriptService.run();
		}
		catch (error) {

			document.querySelector("body")?.setAttribute("data-error", (error as Error)?.message);
		}
		finally {

			window.addEventListener("load", function () {

				if (pageLoaderTimeout) clearTimeout(pageLoaderTimeout);
				self.loaderService.hidePageLoader();
			});
		}

		window.onerror = function (event, source, lineno, colno, error) {

			document.querySelector("body")?.setAttribute("data-error", error?.message ?? "");
		}
	}

	/**
	 * @inheritdoc
	 */
	applyEffects(element: Element) {

		this.setupErrorHandling();

		try {

			this.formService.applyAll(element);
			this.tierSelectorService.applyGlobalTierSelectors(element);
			this.wizardService.applyAll(element);
			this.dataGridService.applyAll(element);
			this.messageService.applyEffects(element);
			this.userTierService.applyTierLinks();
		}
		catch (error) {

			this.logError(error as Error);
		}
	}

	/**
	 * Applies search functionalithy to the page, by locating the search form and
	 * adding an on submit event listener, which navigates to the search page.
	 */
	applySearch() {

		document.getElementById("search-form")?.addEventListener("submit", event => {

			const searchValue = (document.getElementById("search-input") as HTMLInputElement)?.value.trim();

			if (searchValue) {
				window.location.href = this.urlService.url(`Search?query=${encodeURIComponent(searchValue)}`)
			}

			event.preventDefault();
		});
	}

	/**
	 * Setup client-side error handling for the application.
	 * Logs errors to the console and optionally the server.
	 */
	private setupErrorHandling() {

		if (!this.ipfModule.context.clientLoggingDisabled && this.ipfModule.context.userId > 0) {

			// add on error handler to the window, every unhandled JS exception will trigger a callback to this function
			window.onerror = (message, source, lineno, colno, error) => {

				try {

					let msg: string;
					let detail: string;

					if (typeof message === "string") {

						msg = message;
						detail = `Source: ${source}\r\nLine: ${lineno}\r\nColumn: ${colno}\r\n\r\nStack trace:\r\n${error?.stack}`;
					}
					else {

						msg = error?.message ?? "";
						detail = `Stack trace:\r\n${error?.stack}`;
					}

					this.logErrorInternal(msg, detail);
				}
				catch (error) {

					console.error(error);
				}
			};
		}
	}

	/**
	 * Logs the given error to the console and optionally to the server, when clientLoggingDisabled is not disabled.
	 * @param error The error to log.
	 */
	private logError(error: Error) {

		console.error(error);

		if (!this.ipfModule.context.clientLoggingDisabled && this.ipfModule.context.userId > 0) {

			try {

				this.logErrorInternal(error.message, `Stack trace:\r\n${error?.stack}`);
			}
			catch (error) {

				console.error(error);
			}
		}
	}

	/**
	 * Performs an AJAX request to log a message with details.
	 * @param message The error message.
	 * @param detail The error details.
	 */
	private logErrorInternal(message: string, detail: string) {

		this.ajaxService.request({
			url: this.urlService.url("/Log"),
			method: HttpMethods.Post,
			data: {
				level: NcqLogLevel.Error,
				logType: "Portal-Client",
				message: message,
				detail: detail
			} as LogMessage
		});
	}
}